import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { reportingApi } from '../../../services/api';
import { useNotifications } from '../../../context/NotificationContext';

const toCamelCase = (name) => {
  if (!name) return name;
  if ([...name].every(c => (c >= 'A' && c <= 'Z') || c === '_')) return name.toLowerCase();
  return name.charAt(0).toLowerCase() + name.slice(1);
};

// Measure text width using an offscreen canvas (cached per font)
const measureCanvas = document.createElement('canvas').getContext('2d');
const measureTextWidth = (text, font = '13px sans-serif') => {
  measureCanvas.font = font;
  return measureCanvas.measureText(text).width;
};

/**
 * Multi-select combobox for report parameters.
 * - Loads available options from API (FK display values or distinct raw values)
 * - Supports free-text entry with operator (contains, startsWith, endsWith, equals)
 * - Picked-from-list values auto-set to "equals"
 * - Multiple selections rendered as removable chips
 * - All selections OR'd together in the query
 * - Auto-sizes input width to longest option value
 * - Pinned "All" and "Blank" options at top of every dropdown
 *   - "All" replaces all other selections (exclusive); picking a regular item clears "All"
 *   - "Blank" filters for null/empty values; coexists with other selections
 */
const ReportParameterCombobox = ({ param, selections, onChange, reportId, activeFilters, initialOptions = [], initialWidth = 180 }) => {
  const { notify } = useNotifications();
  const [options, setOptions] = useState(initialOptions);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [operator, setOperator] = useState(param.defaultOperator || 'contains');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Special selection state
  const isAllSelected   = selections.some(s => s.id === '__all__');
  const isBlankSelected = selections.some(s => s.id === '__blank__');

  // Build filters payload excluding this parameter's own selections
  const filtersKey = useMemo(() => {
    if (!activeFilters) return '';
    const relevant = {};
    Object.entries(activeFilters).forEach(([field, sels]) => {
      if (field === param.field) return;
      // "All" means no constraint — exclude from downstream filters same as runReport
      const filtered = (sels || []).filter(s => s.id !== '__all__');
      if (filtered.length === 0) return;
      const camelField = toCamelCase(field);
      relevant[camelField] = filtered.map(s => ({ operator: s.operator, value: s.value }));
    });
    return JSON.stringify(relevant);
  }, [activeFilters, param.field]);

  const [optionsLoaded, setOptionsLoaded] = useState(initialOptions.length > 0);

  const loadOptions = useCallback(() => {
    setOptionsLoading(true);
    const filterObj = filtersKey && filtersKey !== '{}' ? JSON.parse(filtersKey) : null;
    reportingApi.getParameterOptions(reportId, param.field, filterObj)
      .then(res => { setOptions(res.data || []); setOptionsLoaded(true); })
      .catch(() => setOptions([]))
      .finally(() => setOptionsLoading(false));
  }, [reportId, param.field, filtersKey]);

  useEffect(() => {
    if (isOpen) {
      loadOptions();
    } else {
      setOptionsLoaded(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey]);

  // Pre-load options when "All" is selected so dropdown is instant on open
  useEffect(() => {
    if (isAllSelected && !optionsLoaded) {
      loadOptions();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAllSelected]);

  // Calculate input width based on longest option + checkbox + padding.
  // initialWidth is pre-computed by the parent before first render and acts as a floor
  // so the container is never undersized even if options haven't loaded yet.
  const inputWidth = useMemo(() => {
    if (options.length === 0) return initialWidth;
    const longestText = options.reduce((longest, opt) => {
      const display = String(opt.display || '');
      return display.length > longest.length ? display : longest;
    }, '');
    const textWidth = measureTextWidth(longestText, '13px sans-serif');
    const totalWidth = Math.ceil(textWidth) + 64;
    return Math.max(initialWidth, Math.min(totalWidth, 500));
  }, [options, initialWidth]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setHighlightIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter and sort options based on input
  const filteredOptions = options
    .filter(opt => {
      const display = String(opt.display || '').toLowerCase();
      const search = inputValue.toLowerCase();
      if (!search) return true;
      return display.includes(search);
    })
    .sort((a, b) => String(a.display || '').localeCompare(String(b.display || '')));

  const MAX_SELECTIONS = 4;

  const isSelected = useCallback((opt) => {
    if (isAllSelected) return true;
    return selections.some(s => s.type === 'picked' && String(s.id) === String(opt.id));
  }, [selections, isAllSelected]);

  // Select "All" — exclusive, clears everything else
  const toggleAll = () => {
    if (isAllSelected) {
      onChange(selections.filter(s => s.id !== '__all__'));
    } else {
      onChange([{ type: 'special', id: '__all__', display: 'All', operator: 'all', value: '__all__' }]);
    }
    inputRef.current?.focus();
  };

  // Select "Blank" — coexists with others, clears "All" if present
  const toggleBlank = () => {
    if (isBlankSelected) {
      onChange(selections.filter(s => s.id !== '__blank__'));
    } else {
      const withoutAll = selections.filter(s => s.id !== '__all__');
      const willExceed = withoutAll.length >= MAX_SELECTIONS;
      onChange([...withoutAll, { type: 'special', id: '__blank__', display: 'Blank', operator: 'blank', value: '__blank__' }]);
      if (willExceed) {
        setTimeout(() => notify.warning(`More than 4 values selected for "${param.label}" — PDF header may not display all selections.`), 0);
      }
    }
    inputRef.current?.focus();
  };

  // Add a picked selection — clears "All" if present
  const addPicked = (opt) => {
    if (isAllSelected) {
      // Switch from "All" to this specific item only
      onChange([{
        type: 'picked',
        id: opt.id,
        display: opt.display,
        fullDisplay: opt.fullDisplay || null,
        operator: 'equals',
        value: String(opt.id)
      }]);
      setInputValue('');
      inputRef.current?.focus();
      return;
    }
    const withoutAll = selections.filter(s => s.id !== '__all__');
    const alreadyPicked = withoutAll.some(s => s.type === 'picked' && String(s.id) === String(opt.id));
    if (alreadyPicked) {
      onChange(withoutAll.filter(s => !(s.type === 'picked' && String(s.id) === String(opt.id))));
    } else {
      const willExceed = withoutAll.length >= MAX_SELECTIONS;
      onChange([...withoutAll, {
        type: 'picked',
        id: opt.id,
        display: opt.display,
        fullDisplay: opt.fullDisplay || null,
        operator: 'equals',
        value: String(opt.id)
      }]);
      if (willExceed) {
        setTimeout(() => notify.warning(`More than 4 values selected for "${param.label}" — PDF header may not display all selections.`), 0);
      }
    }
    setInputValue('');
    inputRef.current?.focus();
  };

  // Add a free-text selection — clears "All" if present
  const addFreeText = () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;

    const withoutAll = selections.filter(s => s.id !== '__all__');
    const willExceed = withoutAll.length >= MAX_SELECTIONS;

    const isDuplicate = withoutAll.some(s =>
      s.type === 'freetext' && s.value === trimmed && s.operator === operator
    );
    if (isDuplicate) return;

    onChange([...withoutAll, {
      type: 'freetext',
      id: null,
      display: `${operator}: "${trimmed}"`,
      operator: operator,
      value: trimmed
    }]);
    if (willExceed) {
      setTimeout(() => notify.warning(`More than 4 values selected for "${param.label}" — PDF header may not display all selections.`), 0);
    }
    setInputValue('');
    inputRef.current?.focus();
  };

  const removeSelection = (index) => onChange(selections.filter((_, i) => i !== index));

  const clearAll = () => { onChange([]); setInputValue(''); };

  // Keyboard nav — offset by 2 for the pinned All/Blank items
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightIndex === 0) { toggleAll(); setHighlightIndex(-1); }
      else if (highlightIndex === 1) { toggleBlank(); setHighlightIndex(-1); }
      else if (highlightIndex >= 2 && highlightIndex - 2 < filteredOptions.length) {
        addPicked(filteredOptions[highlightIndex - 2]);
        setHighlightIndex(-1);
      } else if (inputValue.trim()) {
        addFreeText();
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setHighlightIndex(-1);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen) { setIsOpen(true); if (!optionsLoaded) loadOptions(); }
      setHighlightIndex(prev => Math.min(prev + 1, filteredOptions.length + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Backspace' && !inputValue && selections.length > 0) {
      removeSelection(selections.length - 1);
    }
  };

  useEffect(() => {
    if (highlightIndex >= 0 && dropdownRef.current) {
      const item = dropdownRef.current.children[highlightIndex];
      if (item) item.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightIndex]);

  const operators = param.operators || ['contains', 'startsWith', 'endsWith', 'equals'];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: `${inputWidth + 94}px` }}>
      <label style={{ fontSize: '12px', fontWeight: 600, color: '#555' }}>{param.label}</label>

      <div ref={containerRef} style={{ position: 'relative' }}>
        {/* Operator + Input row */}
        <div style={{ display: 'flex', gap: '4px', marginBottom: selections.length > 0 ? '6px' : 0 }}>
          <select
            value={operator}
            onChange={(e) => setOperator(e.target.value)}
            style={{
              padding: '6px 6px', borderRadius: '4px', border: '1px solid #ccc',
              fontSize: '11px', background: '#fff', flexShrink: 0, width: '90px'
            }}
          >
            {operators.map(op => (
              <option key={op} value={op}>{op}</option>
            ))}
          </select>

          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: '3px', alignItems: 'center',
            flex: 1, border: '1px solid #ccc', borderRadius: '4px', padding: '3px 6px',
            background: '#fff', minHeight: '30px', cursor: 'text',
            outline: isOpen ? '2px solid #1f497d' : 'none'
          }}
            onClick={() => inputRef.current?.focus()}
          >
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value);
                setIsOpen(true);
                if (!optionsLoaded) loadOptions();
                setHighlightIndex(-1);
              }}
              onFocus={() => { setIsOpen(true); if (!optionsLoaded) loadOptions(); }}
              onKeyDown={handleKeyDown}
              placeholder={selections.length === 0 ? (optionsLoading ? 'Loading...' : `Type or select ${param.label}...`) : 'Selected'}
              style={{
                border: 'none', outline: 'none', fontSize: '13px', flex: 1,
                minWidth: '80px', padding: '2px 0', background: 'transparent'
              }}
            />
          </div>

          {selections.length > 0 && (
            <button
              onClick={clearAll}
              title="Clear all"
              style={{
                padding: '4px 8px', border: '1px solid #ccc', borderRadius: '4px',
                background: '#f5f5f5', cursor: 'pointer', fontSize: '12px', color: '#666',
                flexShrink: 0
              }}
            >
              Clear
            </button>
          )}
        </div>

        {/* Dropdown */}
        {isOpen && (
          <div
            ref={dropdownRef}
            style={{
              position: 'absolute', top: '100%', left: '94px',
              width: `${inputWidth}px`,
              maxHeight: '250px', overflowY: 'auto',
              background: '#fff', border: '1px solid #ccc', borderRadius: '4px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 1000,
              marginTop: '2px'
            }}
          >
            {/* Pinned: All */}
            <SpecialOption
              label="All"
              selected={isAllSelected}
              highlighted={highlightIndex === 0}
              onClick={toggleAll}
              onMouseEnter={() => setHighlightIndex(0)}
              onMouseLeave={() => setHighlightIndex(-1)}
            />

            {/* Pinned: Blank */}
            <SpecialOption
              label="Blank"
              selected={isBlankSelected}
              highlighted={highlightIndex === 1}
              onClick={toggleBlank}
              onMouseEnter={() => setHighlightIndex(1)}
              onMouseLeave={() => setHighlightIndex(-1)}
            />

            {/* Separator */}
            <div style={{ borderBottom: '2px solid #e5e7eb', margin: '2px 0' }} />

            {/* Free text entry hint */}
            {inputValue.trim() && (
              <div
                onClick={addFreeText}
                style={{
                  padding: '8px 12px', cursor: 'pointer', fontSize: '12px',
                  borderBottom: '1px solid #eee', background: '#fffbeb', color: '#856404'
                }}
                onMouseEnter={(e) => e.target.style.background = '#fef3cd'}
                onMouseLeave={(e) => e.target.style.background = '#fffbeb'}
              >
                Add as filter: <strong>{operator}</strong> "{inputValue.trim()}"
              </div>
            )}

            {optionsLoading ? (
              <div style={{ padding: '12px', color: '#999', fontSize: '12px', textAlign: 'center' }}>
                Loading options...
              </div>
            ) : filteredOptions.length === 0 ? (
              <div style={{ padding: '12px', color: '#999', fontSize: '12px', textAlign: 'center' }}>
                {inputValue ? 'No matching options' : 'No options available'}
              </div>
            ) : (
              filteredOptions.slice(0, 100).map((opt, idx) => {
                const selected = isAllSelected || isSelected(opt);
                const highlighted = idx + 2 === highlightIndex;
                return (
                  <div
                    key={opt.id}
                    onClick={() => addPicked(opt)}
                    style={{
                      padding: '6px 12px', cursor: 'pointer', fontSize: '13px',
                      display: 'flex', alignItems: 'center', gap: '8px',
                      background: highlighted ? '#e3edf7' : selected ? '#f0f7ff' : '#fff',
                      borderBottom: '1px solid #f5f5f5'
                    }}
                    onMouseEnter={(e) => {
                      setHighlightIndex(idx + 2);
                      if (!selected) e.currentTarget.style.background = '#f0f4f8';
                    }}
                    onMouseLeave={(e) => {
                      if (!selected && !highlighted) e.currentTarget.style.background = '#fff';
                    }}
                  >
                    <span style={{
                      width: '16px', height: '16px', borderRadius: '3px', flexShrink: 0,
                      border: selected ? '2px solid #1f497d' : '2px solid #ccc',
                      background: selected ? '#1f497d' : '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '10px', color: '#fff'
                    }}>
                      {selected && '\u2713'}
                    </span>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {opt.display}
                    </span>
                  </div>
                );
              })
            )}
            {filteredOptions.length > 100 && (
              <div style={{ padding: '8px 12px', color: '#999', fontSize: '11px', textAlign: 'center', borderTop: '1px solid #eee' }}>
                Showing first 100 of {filteredOptions.length} — type to narrow results
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Pinned special option row (All / Blank)
const SpecialOption = ({ label, selected, highlighted, onClick, onMouseEnter, onMouseLeave }) => (
  <div
    onClick={onClick}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    style={{
      padding: '6px 12px', cursor: 'pointer', fontSize: '13px',
      display: 'flex', alignItems: 'center', gap: '8px',
      background: highlighted ? '#e3edf7' : selected ? '#f0f7ff' : '#fff',
      borderBottom: '1px solid #f5f5f5'
    }}
  >
    <span style={{
      width: '16px', height: '16px', borderRadius: '3px', flexShrink: 0,
      border: selected ? '2px solid #1f497d' : '2px solid #ccc',
      background: selected ? '#1f497d' : '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '10px', color: '#fff'
    }}>
      {selected && '\u2713'}
    </span>
    <span style={{ fontWeight: 600, color: '#374151' }}>{label}</span>
  </div>
);

export default ReportParameterCombobox;
